/**
 * The small line icons, in one place — the structure-editing three, the photo dropzone's
 * camera, and the maintenance row's thumbs-up.
 *
 * EXTRACTED FROM `structure-menu.tsx` when the option picker needed them too: the picker
 * cannot import from the menu, because the menu already imports `anchorOf` from the picker,
 * and a cycle between two components is the kind of thing that works in dev and produces an
 * undefined component in a production bundle.
 *
 * Sized and stroked to match `nav.tsx`'s icons, which are the house style.
 */
export function Icon({
  name,
  size = 16,
}: {
  name: 'pencil' | 'cross' | 'plus' | 'camera' | 'thumb' | 'check' | 'back';
  size?: number;
}): JSX.Element {
  const paths = {
    pencil: <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z" />,
    cross: (
      <>
        <path d="M6 6l12 12" />
        <path d="M18 6 6 18" />
      </>
    ),
    plus: (
      <>
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </>
    ),
    // THE ITEM HERO'S BACK ARROW (0.1.526). It was the text glyph "←", which sits on the font's
    // baseline: in a phone's own font it rides LOW in the 40px circle, and the circle reads as a
    // taller pill with the arrow at its foot (maintainer, 2 Oct 2026: "the arrow icon … at the
    // bottom side of this vertical pill-shaped form"). A drawn arrow is centred by geometry, not
    // by font metrics.
    back: (
      <>
        <path d="M19 12H5" />
        <path d="M11 6 5 12l6 6" />
      </>
    ),
    // The success banner's tick (0.1.522, components/success-banner.tsx) — same stroke family.
    check: <path d="M5 12.5 10 17.5 19 7" />,
    // The empty item hero's "Camera or gallery" dropzone (frame 18-2c): body + lens, one stroke
    // weight with the other three so it reads as the same family.
    camera: (
      <>
        <path d="M3 8h3l1.5-2h9L18 8h3v11H3V8Z" />
        <circle cx="12" cy="13" r="3" />
      </>
    ),
    // A thumbs-up — "this job is done" on a maintenance row (maintainer, 27 Aug 2026). The
    // cuff is a separate path so the shape survives at 18px, where a single outline closes up.
    thumb: (
      <>
        <path d="M7 10.5 11 3a2.2 2.2 0 0 1 2.2 2.2V9h4.6A2.2 2.2 0 0 1 20 11.5l-1.2 6A2.2 2.2 0 0 1 16.6 19H7" />
        <path d="M7 10.5H4.5v8.5H7Z" />
      </>
    ),
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}
